/**
 * Ops platform settings — configure delivery charge, platform fee,
 * free-delivery threshold and minimum order amount at runtime.
 *
 * Backed by GET/PUT /api/operations/settings/platform. Values are stored
 * in paise; UI shows/edits in whole rupees for humans, converts on save.
 * Store admins can view but not edit — the PUT endpoint rejects them.
 */

import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Cog } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/features/auth/api";
import { useAuth } from "@/features/auth/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Settings = {
    delivery_charge: number;
    free_delivery_threshold: number;
    platform_fee: number;
    min_order_amount: number;
};

const toRupees = (paise: number) => Math.round((paise ?? 0) / 100);
const toPaise = (rupees: number) => Math.round((rupees ?? 0) * 100);

export default function OpsSettings() {
    const qc = useQueryClient();
    const { user } = useAuth();
    const canEdit = user?.role === "super_admin" || user?.role === "operations_admin";

    const { data } = useQuery({
        queryKey: ["ops", "settings", "platform"],
        queryFn: async () =>
            (await api.get<Settings>("/operations/settings/platform")).data,
    });
    const [form, setForm] = useState<Settings | null>(null);
    useEffect(() => {
        if (data && !form) setForm(data);
    }, [data, form]);

    const saveMut = useMutation({
        mutationFn: async () => {
            if (!form) return;
            await api.put<Settings>("/operations/settings/platform", form);
        },
        onSuccess: () => {
            toast.success("Platform settings saved");
            qc.invalidateQueries({ queryKey: ["ops", "settings", "platform"] });
        },
        onError: (e: unknown) => toast.error((e as Error).message || "Save failed"),
    });

    const current = form ?? data;

    return (
        <div data-testid="ops-settings" className="space-y-4">
            <header className="flex items-center gap-2">
                <Cog className="h-5 w-5 text-brand" />
                <h1 className="text-2xl font-extrabold">Platform settings</h1>
            </header>
            <p className="text-sm text-muted-foreground">
                Live values used by every new checkout. Existing orders keep the fees frozen
                at the moment they were placed.
            </p>

            <section className="grid gap-4 rounded-2xl border border-border/70 bg-card p-5 sm:grid-cols-2">
                <div>
                    <Label>Delivery charge (₹)</Label>
                    <Input
                        type="number"
                        min={0}
                        disabled={!canEdit}
                        value={current ? toRupees(current.delivery_charge) : 0}
                        onChange={(e) =>
                            setForm({
                                ...(current as Settings),
                                delivery_charge: toPaise(Number(e.target.value)),
                            })
                        }
                        data-testid="ops-delivery-charge"
                    />
                </div>
                <div>
                    <Label>Free delivery above (₹)</Label>
                    <Input
                        type="number"
                        min={0}
                        disabled={!canEdit}
                        value={current ? toRupees(current.free_delivery_threshold) : 0}
                        onChange={(e) =>
                            setForm({
                                ...(current as Settings),
                                free_delivery_threshold: toPaise(Number(e.target.value)),
                            })
                        }
                        data-testid="ops-free-delivery-threshold"
                    />
                </div>
                <div>
                    <Label>Platform fee (₹)</Label>
                    <Input
                        type="number"
                        min={0}
                        disabled={!canEdit}
                        value={current ? toRupees(current.platform_fee) : 0}
                        onChange={(e) =>
                            setForm({
                                ...(current as Settings),
                                platform_fee: toPaise(Number(e.target.value)),
                            })
                        }
                        data-testid="ops-platform-fee"
                    />
                </div>
                <div>
                    <Label>Minimum order (₹)</Label>
                    <Input
                        type="number"
                        min={0}
                        disabled={!canEdit}
                        value={current ? toRupees(current.min_order_amount) : 0}
                        onChange={(e) =>
                            setForm({
                                ...(current as Settings),
                                min_order_amount: toPaise(Number(e.target.value)),
                            })
                        }
                        data-testid="ops-min-order"
                    />
                </div>
            </section>

            {canEdit ? (
                <button
                    type="button"
                    onClick={() => saveMut.mutate()}
                    disabled={saveMut.isPending || !form}
                    data-testid="ops-settings-save"
                    className="rounded-full bg-brand px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                >
                    Save changes
                </button>
            ) : (
                <p className="text-xs text-muted-foreground">
                    Read-only for your role. Ask an Ops or Super Admin to modify.
                </p>
            )}
        </div>
    );
}
